import { ChangeDetectorRef, Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import {
    AddressModel,
    AppNotificationService,
    AppNotificationType,
    assignModelOptions,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigConstants,
    CONNECTION_ERROR_CODES,
    EventService,
    isDefined,
    MATERIAL_STATUS_CODE,
    MaterialCentralReturnModel,
    MaterialModel,
    MaterialService,
    MaterialUtilitiesService,
    ModalClass,
    NOTIFICATION_STATUS_CODE,
    OdataMaterialService,
    SynchronizationService,
    ValueHelperInstanceToken,
    ValueHelperInterface,
    YesNoModalDialogComponent,
    YesNoModalDialogModel,
    ConfigInstanceToken,
    ConfigInterface,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { faBuilding, faEllipsisV, faShippingFast, faTaxi, faUsers } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import moment from 'moment';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { combineLatest, forkJoin, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, exhaustMap, map, mergeMap, take, takeUntil, tap } from 'rxjs/operators';
import MaterialReturnP58Model from './material-return-p58.model';
import MaterialReturnBootstockModel from '../material-return-bootstock/material-return-bootstock.model';

@Component({
    selector: 'pace-material-return-p58',
    templateUrl: './material-return-p58.component.html',
    styleUrls: ['./material-return-p58.component.scss'],
    standalone: false,
})
export class MaterialReturnP58Component implements OnInit, OnDestroy {
    public model: MaterialReturnP58Model;
    public bootstockMaterial?: MaterialModel; // Optional material for bootstock return.
    public materialReturnForm: FormGroup;
    public materialReturnChildren: FormArray;
    public deliveryReturnOption = ConfigConstants.DeliveryReturnOptions;
    public isLoading = false;
    @Input() public isBootstockReturn = false;
    @Input() public isBootstockScrap = false;
    public disableSendReturn = false;
    public displayAdditionalMaterialReturnInformation = false;
    public reverse = false;
    public orderBy = '';
    private selectedMaterialsList: MaterialCentralReturnModel[] = [];
    private selectedSpecialDangerousGoodsList: MaterialCentralReturnModel[] = [];
    private isSpecialDangerousGoodsReturnAcknowledged = false;
    protected readonly NOTIFICATION_STATUS_CODE = NOTIFICATION_STATUS_CODE;
    public faUsers = faUsers;
    public faTaxi = faTaxi;
    public faBuilding = faBuilding;
    public faEllipsisV = faEllipsisV;
    public faShippingFast = faShippingFast;
    public readonly displayRoom: boolean;
    public readonly displayBuilding: boolean;
    public readonly displayFloor: boolean;
    public readonly byBoxKonnectLink: string;
    private saveMaterialReturn$ = new Subject<void>();
    private destroy$ = new Subject<void>();
    public bootstockSubmitSuccess$ = new Subject<void>();
    public readonly customerAddress = ConfigConstants.returnCustomer;
    public readonly dopAddress = ConfigConstants.returnDop;
    public readonly taxiAddress = ConfigConstants.returnTaxi;
    public readonly fedexAddress = ConfigConstants.returnFedex;

    public constructor(
        private readonly appNotificationService: AppNotificationService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly changeDetector: ChangeDetectorRef,
        private readonly translate: TranslateService,
        private readonly formBuilder: FormBuilder,
        public readonly modalInstance: BsModalRef,
        private readonly modalService: BsModalService,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
        @Inject(ValueHelperInstanceToken) private readonly valueHelperService: ValueHelperInterface,
        private readonly materialService: MaterialService,
        private readonly odataMaterialService: OdataMaterialService,
        private readonly synchronizationService: SynchronizationService,
        private readonly materialUtilities: MaterialUtilitiesService,
        private readonly eventService: EventService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
    ) {
        this.displayAdditionalMaterialReturnInformation = configService.getDisplayAdditionalMaterialReturnInformation();
        this.displayRoom = this.clientManager.isNgmP58();
        this.displayBuilding = true;
        this.displayFloor = true;
        this.byBoxKonnectLink = this.configService.getByBoxKonnectLink();
    }

    public ngOnInit(): void {
        this.materialReturnChildren = this.formBuilder.array([]);
        this.materialReturnForm = this.formBuilder.group({
            children: this.materialReturnChildren,
        });
        if (this.isBootstockScrap) {
            this.model.materialReturnList.forEach((materialReturn) => {
                materialReturn.material.isRowSelected.value = true;
            });
        }
        this.setSelectedMaterialsList();

        this.saveMaterialReturn$
            .asObservable()
            .pipe(
                exhaustMap(() => this.saveReturnPart()),
                tap(() => {
                    this.log.debug(
                        `MaterialReturnP58Component: saveReturnPart: ${this.selectedMaterialsList.length} materials`,
                    );
                    this.model.isInfoSaved = true;
                    this.disableSendReturn = false;
                }),
                takeUntil(this.destroy$),
            )
            .subscribe();
    }

    public ngOnDestroy(): void {
        this.bootstockSubmitSuccess$.complete();
        this.eventService.triggerRefreshMaterialReturnCount();
        this.destroy$.next();
    }
    public onAddressOptionChanged(): void {
        this.updateValueAndValidity();
    }

    public saveMaterialReturn(): void {
        this.saveMaterialReturn$.next();
    }

    private setSelectedMaterialsList(): void {
        this.selectedMaterialsList = this.model.materialReturnList.filter(
            (materialReturnModel) => materialReturnModel.material.isRowSelected.value,
        );
        this.selectedSpecialDangerousGoodsList = this.selectedMaterialsList.filter((materialReturnModel) =>
            this.materialUtilities.isSpecialDangerousGoodsReturn(materialReturnModel.material),
        );

        this.model.selectedMaterialsNumber = this.selectedMaterialsList.length;
        this.model.updateAWBPackagingModelOptions();
    }

    private handleSpecialDangerousGoodsReturn(materialCentralReturnModel: MaterialCentralReturnModel): void {
        if (materialCentralReturnModel.material.isRowSelected.value) {
            if (
                this.model.isInfoSaved &&
                !(this.model.deliveryReturnOptions.selectedEntry.model.name === this.deliveryReturnOption.Customer)
            ) {
                // if customer was not selected, then all the materials saved data has to be reset before switching to customer
                this.resetReturnPart()
                    .pipe(
                        take(1),
                        tap(() => (materialCentralReturnModel.material.isRowSelected.value = true)),
                    )
                    .subscribe();
                this.model = this.createMaterialReturnModel();
            }
            // select customer and disable further selection
            this.model.deliveryReturnOptions.selectByPredicate((option) => option.name === this.customerAddress);
            this.model.deliveryReturnOptions = assignModelOptions(
                this.model.deliveryReturnOptions,
                {
                    ...this.model.deliveryReturnOptions.options,
                    readonly: true,
                },
                this.model,
            );
            this.onAddressOptionChanged();
            // select material's customer address from dropdown
            this.model.customerAddressOptions.selectByPredicate((option) =>
                option.model.compareFromAddressModel(materialCentralReturnModel.material.customerAddress),
            );
        } else {
            // on unselect, if no other special dangerous goods exist, then enable the radio buttons
            if (this.selectedSpecialDangerousGoodsList.length === 1) {
                this.model.deliveryReturnOptions = assignModelOptions(
                    this.model.deliveryReturnOptions,
                    {
                        ...this.model.deliveryReturnOptions.options,
                        readonly: false,
                    },
                    this.model,
                );
                this.updateValueAndValidity();
            }
        }
    }

    private updateValueAndValidity(): void {
        this.materialReturnChildren.controls.forEach((control) => {
            control.updateValueAndValidity({
                onlySelf: true,
                emitEvent: false,
            });
        });
    }

    public onMaterialSelectionChange(materialCentralReturnModel: MaterialCentralReturnModel): void {
        // special handling for dangerous goods
        if (this.materialUtilities.isSpecialDangerousGoodsReturn(materialCentralReturnModel.material)) {
            this.handleSpecialDangerousGoodsReturn(materialCentralReturnModel);
        }
        // If the return details is already saved once, then
        // selecting additional material should automatically save details for it
        // un-selecting a material should automatically reset details for it
        if (this.model.isInfoSaved) {
            if (materialCentralReturnModel.material.isRowSelected.value) {
                // save values from model to materialCentralReturnModel (needed for outModel)
                this.setMaterialCentralReturnInfo(materialCentralReturnModel);
                // save values to materialModel
                this.setMaterialModelInfo(materialCentralReturnModel.material);
            } else {
                // reset values to materialModel
                materialCentralReturnModel.resetMaterialModelInfo();
            }
            this.updateMaterials([materialCentralReturnModel.material]).subscribe();
        }
        // update the selectedMaterialsList
        this.setSelectedMaterialsList();
    }

    public resetMaterialReturn(): void {
        const modalInstance = this.modalService.show(YesNoModalDialogComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                model: new YesNoModalDialogModel(
                    <string>this.translate.instant('MATERIAL_RETURN_RESET_TITLE'),
                    <string>this.translate.instant('MATERIAL_RETURN_RESET_MESSAGE'),
                    <string>this.translate.instant('MATERIAL_RETURN_RESET_YES'),
                    <string>this.translate.instant('MATERIAL_RETURN_RESET_NO'),
                ),
            },
            ariaLabelledBy: 'yes-no-modal-title',
        });

        modalInstance.content?.modalResult
            .pipe(
                mergeMap((modalResult) => {
                    if (modalResult) {
                        this.log.debug(
                            `MaterialReturnP58Component: resetMaterialReturn: ${this.selectedMaterialsList.length} materials`,
                        );
                        return combineLatest([of(modalResult), this.resetReturnPart()]);
                    }
                    return combineLatest([of(modalResult), of(undefined)]);
                }),
            )
            .subscribe(([modalResult]) => {
                if (modalResult) {
                    this.model = this.createMaterialReturnModel();
                    this.setSelectedMaterialsList();
                }
            });
    }

    private resetReturnPart(): Observable<unknown> {
        // reset and update values of materialModel
        const materialList = this.selectedMaterialsList.map((materialCentralReturnModel) =>
            materialCentralReturnModel.resetMaterialModelInfo(),
        );
        return this.isBootstockReturn || this.isBootstockScrap ? of(undefined) : this.updateMaterials(materialList);
    }

    public saveReturnPart(): Observable<unknown> {
        const materialList = this.selectedMaterialsList.map((materialCentralReturnModel) => {
            // save values from model to materialCentralReturnModel (needed for outModel)
            this.setMaterialCentralReturnInfo(materialCentralReturnModel);
            // save values to materialModel
            return this.setMaterialModelInfo(materialCentralReturnModel.material);
        });
        return this.isBootstockReturn || this.isBootstockScrap ? of(undefined) : this.updateMaterials(materialList);
    }

    private setMaterialCentralReturnInfo(materialCentralReturnModel: MaterialCentralReturnModel): void {
        materialCentralReturnModel.deliveryReturnOptions.selectByPredicate(
            (entry) => entry.model.name === this.model.deliveryReturnOptions.selectedEntry.model.name,
        );
        if (
            this.model.deliveryReturnOptions.selectedEntry.model.name === ConfigConstants.DeliveryReturnOptions.Customer
        ) {
            materialCentralReturnModel.dopAddress = this.model.customerAddressOptions.selectedEntry.model;
        }
        if (this.model.deliveryReturnOptions.selectedEntry.model.name === ConfigConstants.DeliveryReturnOptions.Dop) {
            materialCentralReturnModel.dropOffPointOptions.selectByPredicate(
                (entry) =>
                    entry.model.dropOffPoint.value ===
                    this.model.dropOffPointOptions.selectedEntry.model.dropOffPoint.value,
            );
            materialCentralReturnModel.dopAddress = this.model.dropOffPointOptions.selectedEntry.model;
        }
        materialCentralReturnModel.returnInfo.value = this.model.returnInfo.value;
        materialCentralReturnModel.noPhysicalReturn.value = this.model.noPhysicalReturn.value;

        if (this.model.isAWBPackagingEnabled) {
            materialCentralReturnModel.materialReturnPartSet.value.forEach(
                (part) => (part.awbPackaging.value = this.model.awbPackaging.value),
            );
            materialCentralReturnModel.awbPackaging.value = this.model.awbPackaging.value;
        }
    }

    private setMaterialModelInfo(material: MaterialModel): MaterialModel {
        if (
            this.model.deliveryReturnOptions.selectedEntry.model.name === ConfigConstants.DeliveryReturnOptions.Customer
        ) {
            material.customerAddress = AddressModel.fromCseDopModel(
                this.model.customerAddressOptions.selectedEntry.model,
            );
            material.dopAddress = this.model.customerAddressOptions.selectedEntry.model;
        }
        if (this.model.deliveryReturnOptions.selectedEntry.model.name === ConfigConstants.DeliveryReturnOptions.Dop) {
            material.dopAddress = this.model.dropOffPointOptions.selectedEntry.model;
        }
        material.deliveryReturnSelectedOption = this.model.deliveryReturnOptions.selectedEntry.model.name;
        material.dropOffPointSelectedOption = this.model.dropOffPointOptions.selectedEntry
            ? this.model.dropOffPointOptions.selectedEntry.model.dropOffPoint.value
            : '';
        material.returnInfoText = this.model.returnInfo.value;
        material.noPhysicalReturn = this.model.noPhysicalReturn.value;
        if (this.model.isAWBPackagingEnabled) {
            material.awbPackagingText = this.model.awbPackaging.value;
        }
        return material;
    }

    private updateMaterials(materialList: MaterialModel[]): Observable<unknown> {
        return this.materialService.updateMaterials(materialList).pipe(
            catchError((error) => {
                this.log.error(`MaterialReturnP58Component: updateMaterials: error: ${JSON.stringify(error)}`);
                this.appNotificationService.setAppNotification(
                    AppNotificationType.ERROR,
                    <string>this.translate.instant('MATERIAL_RETURN_SAVE_ERROR_TITLE'),
                    <string>this.translate.instant('MATERIAL_RETURN_SAVE_ERROR_BODY'),
                );
                return of(undefined);
            }),
        );
    }

    public sendReturn(): void {
        if (this.selectedSpecialDangerousGoodsList.length > 0 && !this.isSpecialDangerousGoodsReturnAcknowledged) {
            return this.showSpecialDangerousGoodsReturnPopup();
        }
        this.isLoading = true;
        const materialsToBeUpdated: MaterialModel[] = [];
        const selectedRows = this.model.materialReturnList.filter((row) => row.material.isRowSelected.value);

        const submitReturn = selectedRows.map((row) => {
            return this.odataMaterialService.saveMaterialReturn(row).pipe(
                map(() => {
                    row.material.activityDate.value = moment();

                    if (row.material.noPhysicalReturn) {
                        row.material.materialStatus.statusCode = MATERIAL_STATUS_CODE.NOPHYSICALRETURN;
                        row.material.statusString.value = 'MATERIAL_RETURN_NO_PHYSICAL_RETURN_LABEL';
                    } else {
                        row.material.materialStatus.statusCode = MATERIAL_STATUS_CODE.RETURNED;
                        row.material.statusString.value = 'DASHBOARD_SIDEMENU_MATERIAL_HISTORY_STATUS_RETURNED';
                    }
                    materialsToBeUpdated.push(row.material);
                }),
                catchError((error) => {
                    this.log.error(`MaterialReturnP58Component: Error returning material: ${JSON.stringify(error)}`);
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.ERROR,
                        error === CONNECTION_ERROR_CODES.OFFLINE_STORE_SAVE_ERROR
                            ? <string>this.translate.instant('OFFLINE_STORE_ERROR_TITLE')
                            : <string>this.translate.instant('MATERIAL_RETURN_SUBMIT_ERROR_TITLE'),
                        error === CONNECTION_ERROR_CODES.OFFLINE_STORE_SAVE_ERROR
                            ? <string>this.translate.instant('OFFLINE_STORE_ERROR_BODY')
                            : <string>this.translate.instant('MATERIAL_RETURN_SUBMIT_ERROR_BODY', {
                                  materialNumber: row.selectedPartSet.materialNumber.value,
                                  awb: row.selectedPartSet.awb.value,
                              }),
                    );
                    return of(undefined);
                }),
            );
        });
        forkJoin(...submitReturn)
            .pipe(
                mergeMap(() => {
                    // For bootstock materials, add to local storage so they appear in material history
                    // For materials, they already exist in local storage.
                    if (this.isBootstockReturn || this.isBootstockScrap) {
                        return this.materialService.addMaterialsToLocalStorage(materialsToBeUpdated);
                    }
                    return this.materialService.updateMaterials(materialsToBeUpdated);
                }),
                tap(() => {
                    this.isLoading = false;
                    this.model.isInfoSaved = false;
                    if (this.isBootstockReturn || this.isBootstockScrap) {
                        this.bootstockSubmitSuccess$.next();
                    }
                    this.modalInstance.hide();
                    this.log.info(
                        `MaterialReturnP58Component: ${this.selectedMaterialsList.length} materials returned`,
                    );
                }),
                mergeMap(() => {
                    this.log.debug('MaterialReturnP58Component: sendReturn -> sendOfflineData');
                    return this.synchronizationService.sendOfflineData();
                }),
                catchError((error) => {
                    this.log.debug('MaterialReturnP58Component: sendReturn -> sendOfflineData: ERROR');
                    return throwError(error);
                }),
            )
            .subscribe();
    }

    /**
     * A popup to be displayed and acknowledged by user
     * when a material with special dangerous goods indicator (992, 993) is returned
     */
    private showSpecialDangerousGoodsReturnPopup(): void {
        const warningText = this.valueHelperService
            .getDangerousGoodWarning()
            .map((valueHelperModel) => valueHelperModel.descText);
        if (warningText.join('').length > 0) {
            this.appNotificationService.setAppNotification(
                AppNotificationType.WARN,
                <string>this.translate.instant('MATERIAL_RETURN_DANGEROUS_GOODS_WARNING'),
                warningText.join('<br><br>'),
                () => {
                    this.isSpecialDangerousGoodsReturnAcknowledged = true;
                    this.sendReturn();
                },
            );
        } else {
            this.isSpecialDangerousGoodsReturnAcknowledged = true;
            this.sendReturn();
        }
    }

    public setOrderBy(field: string): void {
        if (this.orderBy === field) {
            this.reverse = !this.reverse;
        } else {
            this.orderBy = field;
        }
    }

    public isMaterialPreparedForReturn(): boolean {
        return this.selectedMaterialsList.length > 0 && this.model.isInfoSaved;
    }

    public isMaterialReturnValid(): boolean {
        return this.selectedMaterialsList.length > 0 && this.materialReturnForm.valid;
    }

    /**
     * P58 only
     * This function try to search the material from the material return list if AWB return number matches
     * Also one warning pop up will get displayed in case there is no material with matched AWB return number
     */
    public selectMaterial(): void {
        const awbFormControl = (<FormArray>this.materialReturnForm.controls.children).controls.find(
            (control) => control.value === this.model.awbReturn.value,
        );
        if (awbFormControl && awbFormControl.invalid) {
            return;
        }
        const matchedMaterialReturn = this.model.materialReturnList.find(
            (materialReturn) =>
                !!this.model.awbReturn.value && materialReturn.material.awbReturn.value === this.model.awbReturn.value,
        );

        if (matchedMaterialReturn) {
            matchedMaterialReturn.material.isRowSelected.value = true;
            this.onMaterialSelectionChange(matchedMaterialReturn);
            const awbReturn = _.cloneDeep(this.model.awbReturn);
            awbReturn.value = '';
            this.model.awbReturn = awbReturn;
        } else {
            if (this.model.awbReturn.value) {
                this.appNotificationService.setAppNotification(
                    AppNotificationType.WARN,
                    <string>this.translate.instant('MATERIAL_RETURN_AWB_NOT_FOUND_TITLE'),
                    <string>this.translate.instant('MATERIAL_RETURN_AWB_NOT_FOUND_BODY'),
                );
            }
        }
        this.changeDetector.detectChanges();
    }

    /**
     * If a material was only selected and not saved, then it should be unselected if user clicks on "cancel" or close popup.
     * For bootstock returns, materials are not persisted in local storage yet, so skip the reset/update.
     */
    public closeModal(): void {
        if (!this.model.isInfoSaved && !this.isBootstockReturn && !this.isBootstockScrap) {
            const materialsList = this.model.materialReturnList.map((materialCentralReturnModel) => {
                materialCentralReturnModel.resetMaterialModelInfo();
                return materialCentralReturnModel.material;
            });

            this.updateMaterials(materialsList).subscribe();
        }
        this.modalInstance.hide();
    }

    public validateForm(): void {
        this.changeDetector.detectChanges();
    }

    private createMaterialReturnModel(): MaterialReturnP58Model {
        return isDefined(this.bootstockMaterial) && (this.isBootstockReturn || this.isBootstockScrap)
            ? new MaterialReturnBootstockModel(
                  this.bootstockMaterial,
                  this.materialService.getCSEDopList(),
                  this.materialUtilities,
                  this.configService.getUserFrontId(),
                  this.isBootstockReturn,
              )
            : new MaterialReturnP58Model();
    }
}
